import { OfferPageRoom, OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { arrayToObject, groupBy, min, nonNullable, sum, trimArray, uniqueBy, countBy, sortBy } from 'lib/array/arrayUtils'
import { createSelector } from 'reselect'
import generateOccupancyStringByRoom from 'lib/offer/generateOccupancyStringByRoom'
import { EmptyObject, mapObject, mergeObjects } from 'lib/object/objectUtils'
import {
  CalendarPickerMonthView,
  getCalendarDays as offerPageSelectorsGetCalendarDays,
  getCalendarMonths as offerPageSelectorsGetCalendarMonths,
  getDaysOtherAvailability as offerPageSelectorsGetDaysOtherAvailability,
  getDurationOptions,
  getDurationPackageOptions as offerPageSelectorsGetDurationPackageOptions,
  getMonthsOtherAvailability as offerPageSelectorsGetMonthsOtherAvailability,
  getPackagesForFilters as offerPageSelectorsGetPackagesForFilters,
  getSelectedOfferPageDay as offerPageSelectorsGetSelectedOfferPageDay,
  isFetchingCalendars as offerPageSelectorsIsFetchingCalendars,
  MonthDayView,
  getCheckoutDate,
} from './offerPageSelectors'
import { buildAvailableRateKey } from 'lib/offer/availabilityUtils'
import { OfferDurationOption } from 'components/OfferPage/Filters/DurationTabs/OfferDurationTabs'
import moment from 'moment'
import { addPricing } from 'lib/offer/pricing'
import { SetRequired } from 'type-fest'
import { getPackageUniqueKey } from 'lib/offer/offerUtils'

export interface PackageOptionViewByRoom {
  [roomId: string]: App.OfferPackageOptionView | undefined
}

export interface PackageOptionViewWithRoom {
  roomId: string
  view: App.OfferPackageOptionView
}

export interface PackagesOptionsViewsByRoom {
  [roomId: string]: Array<App.OfferPackageOptionView>
}

export interface LoadingStatusByRoom {
  [roomId: string]: boolean
}

export interface AvailableRatesByRoom {
  [roomId: string]: App.OfferAvailableRates
}

export interface FlightsInfoByRoom {
  [roomId: string]: {
    isFlightsSelected: boolean
    isFlightsAvailable: boolean
    flightPrice: number
  }
}

export interface MultiRoomPackageTotals {
  hotelPrice: number
  hotelMemberPrice: number
  hotelPlusFlightsPrice: number
  hotelPlusFlightsMemberPrice: number
  flightPrice: number
  hotelValue: number
  shouldDisplayValue?: boolean
}

const initialRooms: Array<OfferPageRoom> = []

const getAvailableRatesByRoom = createSelector(
  (state: OfferPageState, appState: App.State) => appState.offer.offerAvailableRatesByOccupancy[state.offer!.id],
  (state: OfferPageState) => state.rooms,
  (state: OfferPageState) => state.checkInDate,
  (state: OfferPageState) => getCheckoutDate(state),
  (availableRatesByOccupancy, rooms = [], checkInDate, checkOutDate): AvailableRatesByRoom => {
    if (availableRatesByOccupancy && checkInDate && checkOutDate) {
      return arrayToObject(
        rooms,
        room => room.id,
        room => {
          const key = buildAvailableRateKey(checkInDate, checkOutDate, [room])
          return {
            ...availableRatesByOccupancy[key],
            key,
          }
        },
      )
    }
    return EmptyObject
  },
)

/**
 * Creates a custom "OfferPageState" and "App.State"
 * for each room
 */
const getOfferAndAppStateByRoom = createSelector(
  (state: OfferPageState) => state,
  (state: OfferPageState, appState: App.State) => appState,
  getAvailableRatesByRoom,
  (state: OfferPageState, appState: App.State, availableRatesByRoom): { [occupancyString: string]: [App.State, OfferPageState] } => {
    const offerId = state.offer!.id
    const roomsStates: Record<string, [App.State, OfferPageState]> = {}
    const rooms = state.rooms ?? []
    rooms.forEach(room => {
      const roomUniqueKey = room.roomId && room.rateId ? getPackageUniqueKey(room.roomId, state.duration, room.rateId) : undefined
      const occupancyString = generateOccupancyStringByRoom(room)
      const occupancyCalendars = appState.calendar.calendarsByOccupancy[occupancyString] ?? {}
      const occupancyCalendarsLoading = appState.calendar.calendarsLoading[occupancyString] ?? false
      const occupancyCalendarsErrors = appState.calendar.calendarErrors[occupancyString]
      const offerAvailableRates = availableRatesByRoom[room.id]
      const selectedPackage = state.offer?.packages.find(pkg => pkg.uniqueKey === roomUniqueKey)

      roomsStates[room.id] = [
        // App.State for this occupancy
        // Puts all the packages calendars of this occupancy inside "calendarsByPackageKey"
        // Makes all the packages have the value of its occupancy for "calendarsLoading" and "calendarErrors"
        {
          ...appState,
          calendar: {
            ...appState.calendar,
            calendarsByPackageKey: occupancyCalendars,
            calendarsLoading: mergeObjects(Object.keys(occupancyCalendars).map(pkgKey => ({ [pkgKey]: occupancyCalendarsLoading }))),
            calendarErrors: mergeObjects(Object.keys(occupancyCalendars).map(pkgKey => ({ [pkgKey]: occupancyCalendarsErrors }))),
          },
          offer: {
            ...appState.offer,
            offerAvailableRates: {
              [offerId]: offerAvailableRates ? { [offerAvailableRates.key]: offerAvailableRates } : {},
            },
          },
        },
        // OfferState for this room
        // We want only this room into the rooms
        {
          ...state,
          rooms: [room],
          selectedPackageKey: selectedPackage?.uniqueKey,
        },
      ]
    })

    return roomsStates
  },
)

/**
 * Returns all package options for each room
 * filtered by the selected duration
 */
export const getDurationPackageOptionsByRoom = createSelector(
  (state: OfferPageState) => state,
  (state: OfferPageState, appState: App.State) => appState,
  (state, appState) => {
    if (!state.rooms) return {}
    const roomsStates = getOfferAndAppStateByRoom(state, appState)
    const durationPackageOptionsByRoom: { [roomId: string]: Array<App.OfferPackageOptionView> } = mergeObjects(
      Object
        .entries(roomsStates)
        .map(([roomId, states]) => ({ [roomId]: offerPageSelectorsGetDurationPackageOptions(states[1], states[0]) })),
    )
    return durationPackageOptionsByRoom
  },
)

export const getValidPackagesOptionsByRoom = createSelector(
  (state: OfferPageState, appState: App.State) => getDurationPackageOptionsByRoom(state, appState),
  (state: OfferPageState) => state.offer,
  (state: OfferPageState) => state.checkInDate,
  (packagesOptionsByRoom, offer, checkIn) => {
    // flag which indicate if this property uses a external providers for checking availability
    const useDynamicCancellationPolicies = offer?.property?.useDynamicCancellationPolicies ?? false
    // Filters only packages with hotel available and
    // valid capacity for each room
    const validPackagesOptionsByRoom = mapObject(
      packagesOptionsByRoom,
      (packageOptions) => (
        packageOptions.filter((pkgOption) =>
          pkgOption.hotelAvailable && pkgOption.capacityValid && !!pkgOption.package.roomType &&
          (!useDynamicCancellationPolicies || !checkIn || !!pkgOption.hasAvailableRates),
        )
      ),
    )

    return validPackagesOptionsByRoom
  },
)

export const getAllPackageOptions = createSelector(
  (state: OfferPageState, appState: App.State) => getValidPackagesOptionsByRoom(state, appState),
  (validPackagesOptionsByRoom) => {
    // get all the package options of all rooms
    const allPkgOptions = arrayToObject(Object.values(validPackagesOptionsByRoom).flat(), (pkgOption) => pkgOption.package.uniqueKey)
    return allPkgOptions
  },
)

export const getTotalAvailabilityByPackage = createSelector(
  (state: OfferPageState, appState: App.State) => getAllPackageOptions(state, appState),
  (allPkgOptions) => {
    const roomTypeOptions = Object.fromEntries(groupBy(allPkgOptions, (pkgOption) => pkgOption.package.roomType.id).entries())

    // Gets the available rooms count of each package.
    // Even if each room has its packages options, there will be
    // options of the same package, and they share the amount of rooms
    // available
    const pkgOptionAvailableRooms = mapObject(allPkgOptions, (pkgOption) => pkgOption.availableRooms || 0)
    const roomTypeAvailableRooms = mapObject(roomTypeOptions, (pkgOptions) => Math.max(...pkgOptions.map(pkgOption => pkgOption.availableRooms || 0)))

    // Since some other selectors will work with a copy of these objects and mutate the copies,
    // freeze them so nobody accidentally mutates the originals.
    Object.freeze(pkgOptionAvailableRooms)
    Object.freeze(roomTypeAvailableRooms)

    return {
      pkgOptionAvailableRooms,
      roomTypeAvailableRooms,
    }
  },
)

/**
 * Returns the active packages by room.
 * Returns the selected package if any and it is available,
 * if there is a package selected but is not available anymore,
 * try to get the cheapest of same room type, else returns the
 * cheapest available, or undefined if none
 */
export const getActivePackagesByRoom = createSelector(
  (state: OfferPageState, appState: App.State) => getValidPackagesOptionsByRoom(state, appState),
  (state: OfferPageState, appState: App.State) => getAllPackageOptions(state, appState),
  (state: OfferPageState, appState: App.State) => getTotalAvailabilityByPackage(state, appState),
  (state: OfferPageState) => state.rooms ?? initialRooms,
  (state: OfferPageState) => state.checkInDate,
  (state: OfferPageState) => state.duration,
  (state: OfferPageState) => state.flights,
  (validPackagesOptionsByRoom, allPkgOptions, availabilities, rooms, checkIn, duration, flights) => {
    // Here we will store the active package for each room,
    // so then we know the amount of rooms used from each
    // package
    const activePackagesByRoom: PackageOptionViewByRoom = {}

    // Important: Spread these to new objects since we'll be mutating them
    const pkgOptionAvailableRooms = { ...availabilities.pkgOptionAvailableRooms }
    const roomTypeAvailableRooms = { ...availabilities.roomTypeAvailableRooms }

    // We want to process the rooms in descending order of occupant count, to ensure rooms with fewer people
    // aren't hogging larger room types that are required by rooms with more people.
    // Among rooms with the same occupant count, handle ones with pre-existing selections first in order to
    // preserve those selections.
    let roomsToProcess = sortBy(
      rooms,
      room => room.roomId ? 1 : -1,
      'desc',
    )
    roomsToProcess = sortBy(
      rooms,
      room => room.adults + (room.teenagers ?? 0) + (room.children ?? 0) + (room.infants ?? 0),
      'desc',
    )

    // First of all, we check for rooms that has a package selected
    roomsToProcess
      .filter((room): room is SetRequired<OfferPageRoom, 'roomId' | 'rateId'> => !!(room.roomId && room.rateId))
      .forEach(room => {
        const roomUniqueKey = getPackageUniqueKey(room.roomId, duration, room.rateId)
        const selectedPackageView = allPkgOptions[roomUniqueKey]
        const isStillValidForRoom = validPackagesOptionsByRoom[room.id].some(
          (pkgOption) => pkgOption.package.uniqueKey === roomUniqueKey,
        )
        const isStillAvailable = selectedPackageView?.package &&
          pkgOptionAvailableRooms[selectedPackageView.package.uniqueKey] > 0 &&
          roomTypeAvailableRooms[selectedPackageView.package.roomType.id] > 0
        const isSelectedPackageKeyValid = isStillValidForRoom && (!checkIn || isStillAvailable)

        // If the room already has a package selected,
        // and it still with capacity valid and hotel available,
        // and with available rooms, then it still a valid selection.
        // So we store this selection into active packages and
        // discount one available room for this package
        if (isSelectedPackageKeyValid) {
          activePackagesByRoom[room.id] = selectedPackageView
          if (checkIn) {
            pkgOptionAvailableRooms[selectedPackageView.package.uniqueKey] -= 1
            roomTypeAvailableRooms[selectedPackageView.package.roomType.id] -= 1
          }
        }
      })

    // Now that we ensures that rooms will keep its previous selections (if valid),
    // we check the other rooms without selections
    roomsToProcess
      .filter(room => !activePackagesByRoom[room.id])
      .forEach(room => {
        // If the room doesn't have any package selected, or its
        // selection becomes invalid (see above), then we will choose
        // the cheapest package available for this room (if any)
        const options = !checkIn ?
          validPackagesOptionsByRoom[room.id] :
            validPackagesOptionsByRoom[room.id].filter(pkgOption =>
              pkgOptionAvailableRooms[pkgOption.package.uniqueKey] > 0 &&
              roomTypeAvailableRooms[pkgOption.package.roomType.id] > 0,
            )
        const cheapestAvailable = min(
          options,
          (pkgOption) => flights ? pkgOption.hotelPrice + pkgOption.flightPrice : pkgOption.hotelPrice,
        )

        activePackagesByRoom[room.id] = cheapestAvailable

        // Discount one available room for this package if
        // it is valid
        if (cheapestAvailable && checkIn) {
          pkgOptionAvailableRooms[cheapestAvailable.package.uniqueKey] -= 1
          roomTypeAvailableRooms[cheapestAvailable.package.roomType.id] -= 1
        }
      })

    return activePackagesByRoom
  },
)

/**
 * Returns for each room its available packages options
 * based on selected duration, available rooms, capacity and hotel available
 */
export const getAvailablePackagesByRoom = createSelector(
  (state: OfferPageState, appState: App.State) => getDurationPackageOptionsByRoom(state, appState),
  (state: OfferPageState, appState: App.State) => getActivePackagesByRoom(state, appState),
  (state: OfferPageState) => state.offer,
  (state: OfferPageState) => state.rooms ?? initialRooms,
  (state: OfferPageState) => state.checkInDate,
  (packagesOptionsByRoom, activePackagesByRoom, offer, rooms, checkIn) => {
    // flag which indicate if this property uses a external providers for checking availability
    const useDynamicCancellationPolicies = offer?.property?.useDynamicCancellationPolicies ?? false
    // Filters only packages with hotel available and
    // valid capacity for each room
    const validPackagesOptionsByRoom = mapObject(
      packagesOptionsByRoom,
      (packageOptions) => (
        packageOptions.filter((pkgOption) =>
          pkgOption.hotelAvailable && pkgOption.capacityValid && !!pkgOption.package.roomType &&
          (!useDynamicCancellationPolicies || !checkIn || !!pkgOption.hasAvailableRates),
        )
      ),
    )

    // get all the package options of all rooms
    const allPkgOptions = arrayToObject(Object.values(validPackagesOptionsByRoom).flat(), (pkgOption) => pkgOption.package.uniqueKey)
    const roomTypeOptions = Object.fromEntries(groupBy(allPkgOptions, (pkgOption) => pkgOption.package.roomType.id).entries())

    // Gets the available rooms count of each package.
    // Even if each room has its packages options, there will be
    // options of the same package, and they share the amount of rooms
    // available
    const pkgOptionAvailableRooms = mapObject(allPkgOptions, (pkgOption) => pkgOption.availableRooms || 0)
    const roomTypeAvailableRooms = mapObject(roomTypeOptions, (pkgOptions) => Math.max(...pkgOptions.map(pkgOption => pkgOption.availableRooms || 0)))

    const activePackages = countBy(Object.values(activePackagesByRoom), (pkgKey) => pkgKey?.package.uniqueKey)

    const availablePackagesByRoom = arrayToObject(
      rooms,
      room => room.id,
      room => (
        validPackagesOptionsByRoom[room.id]
          .map(pkgOption => {
            let availableRoomsByOption = pkgOptionAvailableRooms[pkgOption.package.uniqueKey]
            let availableRoomsByRoomType = roomTypeAvailableRooms[pkgOption.package.roomType.id]

            const activePackageView = activePackagesByRoom[room.id]
            if (activePackageView) {
              // If this package is the one selected for this room (manually selected or cheapest),
              // then we increase one available room (because is the room that is being used, so it must
              // be available)
              const activeOption = allPkgOptions[activePackageView.package.uniqueKey].package
              const activeOptionSelectedCount = activePackages.get(activeOption.uniqueKey) ?? 0
              if (pkgOption.package.uniqueKey === activeOption.uniqueKey) {
                availableRoomsByOption += activeOptionSelectedCount
              }
              if (pkgOption.package.roomType.id === activeOption.roomType.id) {
                availableRoomsByRoomType += activeOptionSelectedCount
              }
            }

            return ({
              ...pkgOption,
              availableRooms: Math.min(availableRoomsByOption, availableRoomsByRoomType),
            })
          })
          // As you might have seen above, if user has no dates selected,
          // no packages will have the "availableRooms" prop
          .filter(pkgOption => !checkIn || pkgOption.availableRooms > 0)
      ),
    )

    return availablePackagesByRoom
  },
)

export const getRemainingAvailabilityByPackage = createSelector(
  (state: OfferPageState) => state.rooms,
  (state: OfferPageState, appState: App.State) => getActivePackagesByRoom(state, appState),
  (state: OfferPageState, appState: App.State) => getTotalAvailabilityByPackage(state, appState),
  (rooms, activePackagesByRoom, availabilities) => {
    // Important: Spread these to new objects since we'll be mutating them
    const pkgOptionAvailableRooms: typeof availabilities.pkgOptionAvailableRooms = { ...availabilities.pkgOptionAvailableRooms }
    const roomTypeAvailableRooms: typeof availabilities.roomTypeAvailableRooms = { ...availabilities.roomTypeAvailableRooms }

    // Subtract allocated rooms from availability
    for (const room of rooms) {
      const selectedPkg = activePackagesByRoom[room.id]?.package
      if (selectedPkg) {
        if (pkgOptionAvailableRooms[selectedPkg.uniqueKey]) {
          pkgOptionAvailableRooms[selectedPkg.uniqueKey] -= 1
        }
        if (roomTypeAvailableRooms[selectedPkg.roomType.id]) {
          roomTypeAvailableRooms[selectedPkg.roomType.id] -= 1
        }
      }
    }

    return {
      pkgOptionAvailableRooms,
      roomTypeAvailableRooms,
    }
  },
)

/**
 * Returns the selected packages by room if it is available
 */
export const getSelectedPackagesByRoom = createSelector(
  getAvailablePackagesByRoom,
  (state: OfferPageState) => state.rooms ?? initialRooms,
  (packageOptionsByRoom, rooms): PackageOptionViewByRoom => {
    return arrayToObject(
      rooms,
      room => room.id,
      room => {
        const roomPackages = packageOptionsByRoom[room.id]
        return roomPackages.find(packageOption => packageOption.package.id === room.roomId && packageOption.package.roomRate.id === room.rateId)
      },
    )
  },
)

/**
 * Returns the cheapest package available by room
 */
export const getCheapestPackagesByRoom = createSelector(
  getAvailablePackagesByRoom,
  (state: OfferPageState) => state.flights,
  (availablePackagesByRoom, flights) => {
    // Note that "getAvailablePackagesByRoom" has a filter for cheapest packages
    // as well so it can adjusts the availability for these packages.
    const packagesByRoom = mapObject(
      availablePackagesByRoom,
      packagesOptions => min(packagesOptions, (pkgOption) => {
        const hotelPriceSelected = pkgOption.baseMemberPrice > 0 ? pkgOption.hotelMemberPrice : pkgOption.hotelPrice
        return flights ? hotelPriceSelected + pkgOption.flightPrice : hotelPriceSelected
      }),
    )

    return packagesByRoom
  },
)

export const getDefaultMultipleRoomPackages = createSelector(
  getActivePackagesByRoom,
  (state: OfferPageState) => state.rooms,
  (activePackages, rooms): Array<PackageOptionViewWithRoom> => {
    return Object.entries(activePackages)
      .filter((entry): entry is [string, App.OfferPackageOptionView] => {
        const [roomId, view] = entry
        return !!view && !rooms.some(room => room.id === roomId) // Does this even make sense? The room will always exist if it came from getActivePackagesByRoom
      })
      .map(([roomId, view]) => ({ roomId, view }))
  },
)

export const getHasPackageChangedFromSelection = createSelector(
  getActivePackagesByRoom,
  (state: OfferPageState) => state.rooms,
  (activePackages, rooms): boolean => {
    if (!rooms) return false
    return rooms.some(room => {
      const activeRoom = activePackages[room.id]
      if (!room.roomId) return false
      return activeRoom?.package.id !== room.roomId || activeRoom?.package.roomRate.id !== room.rateId
    })
  },
)

/**
 * Whether or not there is any variation in what packages are currently
 * chosen for the rooms selected
 */
export const hasDifferentPackagesForRooms = createSelector(
  getActivePackagesByRoom,
  (activePackages) => {
    return new Set(nonNullable(Object.values(activePackages)).map(view => view.package.uniqueKey)).size > 1
  },
)

/**
 * Returns whether the calendar for each room occupancy
 * is loading or not
 */
export const isCalendarsLoadingByRoom = createSelector(
  (state: OfferPageState) => state.rooms,
  (state: OfferPageState) => state.offer?.id,
  (state: OfferPageState, appState: App.State) => appState.calendar.calendarsByOccupancyLoading,
  (rooms = [], offerId = '', calendarsLoading): LoadingStatusByRoom => {
    return arrayToObject(
      rooms,
      room => room.id,
      room => !!calendarsLoading[generateOccupancyStringByRoom(room)]?.[offerId],
    )
  },
)

const isAvailableRatesLoadingByRoom = createSelector(
  (state: OfferPageState) => state.rooms,
  (state: OfferPageState) => state.offer!,
  (state: OfferPageState) => state.checkInDate,
  getAvailableRatesByRoom,
  (rooms = [], offer, checkInDate, availableRatesByRoom): LoadingStatusByRoom => {
    const requiresRates = !!offer?.property?.useDynamicCancellationPolicies && !!checkInDate
    return arrayToObject(rooms,
      room => room.id,
      room => requiresRates && !!availableRatesByRoom[room.id]?.fetching,
    )
  },
)

export const getIsLoadingByRoom = createSelector(
  (state: OfferPageState) => state.rooms,
  isCalendarsLoadingByRoom,
  isAvailableRatesLoadingByRoom,
  (rooms = [], calendarsLoading, availableRatesLoading): LoadingStatusByRoom => {
    return arrayToObject(
      rooms,
      room => room.id,
      room => calendarsLoading[room.id] || availableRatesLoading[room.id],
    )
  },
)

/**
 * Returns whether each room's occupancy calendar
 * needs to be loaded or not
 */
export const getCalendarsToBeFetchedByRoom = createSelector(
  (state: OfferPageState, appState: App.State) => appState.calendar.calendarsByOccupancy,
  (state: OfferPageState) => state,
  (state: OfferPageState, appState: App.State) => appState,
  (calendarsByOccupancy, state, appState): LoadingStatusByRoom => {
    const roomsStates = getOfferAndAppStateByRoom(state, appState)
    const roomsLoading = mergeObjects(
      Object
        .entries(roomsStates)
        .map(([roomId, states]) => ({ [roomId]: offerPageSelectorsIsFetchingCalendars(...states) })),
    )

    return mergeObjects(
      Object
        .keys(roomsLoading)
        .map(roomId => {
          const occupancy = state.rooms?.find(room => room.id === roomId)
          return {
            [roomId]: (occupancy && !calendarsByOccupancy[generateOccupancyStringByRoom(occupancy)]) || roomsLoading[roomId],
          }
        }),
    )
  },
)

/**
 * Returns the calendar months representing the
 * pricing and availability from all rooms
 */
export const getMultiRoomCalendarMonths = createSelector(
  (state: OfferPageState) => state,
  (state: OfferPageState, appState: App.State) => appState,
  (state, appState) => {
    // If multiple room booking is active,
    // we mock OfferPageState and App.State for each room,
    // so we can call the default method to build
    // the calendar months
    const roomsStates = getOfferAndAppStateByRoom(state, appState)
    const calendarMonthsByRoom = mergeObjects(
      Object
        .entries(roomsStates)
        .map(([roomId, states]) => ({ [roomId]: offerPageSelectorsGetCalendarMonths(...states) })),
    )

    // Merges all months into a single object
    const resultMonths: { [month: string]: CalendarPickerMonthView } = {}
    Object
      .entries(calendarMonthsByRoom)
      .map(([, calendar]) => {
        // We change the packagedTotal for each month if flights is selected for this room
        // so the calendar will show the correct price (considering that might have only one
        // room with flights selected)
        return calendar.months.map(month => ({ ...month, packagedTotal: state.flights ? month.packagedTotal : month.hotelTotal }))
      })
      .flat()
      .forEach(month => {
        if (!resultMonths[month.key]) {
          resultMonths[month.key] = month
        } else {
          resultMonths[month.key] = {
            ...resultMonths[month.key],
            hotelAvailable: resultMonths[month.key].hotelAvailable && month.hotelAvailable,
            packagedAvailable: resultMonths[month.key].packagedAvailable && month.packagedAvailable,
            hotelTotal: resultMonths[month.key].hotelTotal + month.hotelTotal,
            packagedTotal: resultMonths[month.key].packagedTotal + month.packagedTotal,
            limitedAvailability: resultMonths[month.key].limitedAvailability && month.limitedAvailability,
            flightCacheActive: resultMonths[month.key].flightCacheActive && month.flightCacheActive,
          }
        }
      })

    return ({
      months: Object.values(resultMonths),
      availableCount: trimArray(Object.values(resultMonths), month => !month.hotelAvailable).length,
    })
  },
)

/**
 * Returns the availability of the months representing
 * the availability from all rooms for each month
 */
export const getMultiRoomMonthsOtherAvailability = createSelector(
  (state: OfferPageState) => state,
  (state: OfferPageState, appState: App.State) => appState,
  (state, appState) => {
    const roomsStates = getOfferAndAppStateByRoom(state, appState)
    const roomsAvailabilities =
      Object
        .values(roomsStates)
        .map(states => offerPageSelectorsGetMonthsOtherAvailability(...states))

    const resultAvailabilities: { [month: string]: boolean } = {}
    roomsAvailabilities
      .map(Object.entries)
      .flat()
      .forEach(([monthKey, isAvailable]) => {
        if (!(monthKey in resultAvailabilities)) {
          resultAvailabilities[monthKey] = isAvailable
        } else {
          resultAvailabilities[monthKey] = resultAvailabilities[monthKey] && isAvailable
        }
      })

    return resultAvailabilities
  },
)

/**
 * Returns the information about the selected check in date (pricing/availability),
 * representing the information from all rooms for the same day
 */
export const getSelectedOfferPageMultiRoomDay = createSelector(
  (state: OfferPageState) => state,
  (state: OfferPageState, appState: App.State) => appState,
  (state, appState) => {
    const roomsStates = getOfferAndAppStateByRoom(state, appState)
    const daysByRoom = mergeObjects(
      Object
        .entries(roomsStates)
        .map(([roomId, states]) => ({ [roomId]: offerPageSelectorsGetSelectedOfferPageDay(...states) })),
    )

    const roomsDays = Object.entries(daysByRoom).filter((roomDay): roomDay is [string, App.CalendarDay] => Boolean(roomDay[1]))

    const resultSelectedDay = roomsDays.length ?
        roomsDays
          .map<App.CalendarDay>(([, roomDay]) => {
            return ({
              ...roomDay,
              packagedTotal: state.flights ? roomDay.packagedTotal : roomDay.hotelTotal,
            })
          })
          .reduce((resultDay, roomDay): App.CalendarDay => {
            if (!roomDay) return resultDay

            return ({
              ...resultDay,
              // We don't care about the uniqueKey here, we just want the
              // prices and availability
              // @ts-ignore
              uniqueKey: undefined,
              // The same for roomId
              // @ts-ignore
              roomId: undefined,
              canRequestDates: resultDay.canRequestDates && roomDay.canRequestDates,
              taxesAndFees: resultDay.taxesAndFees + roomDay.taxesAndFees,
              propertyFees: resultDay.propertyFees + roomDay.propertyFees,
              roomPrices: resultDay.roomPrices.concat(roomDay.roomPrices),
              hotelValue: resultDay.hotelValue + roomDay.hotelValue,
              hotelPrice: resultDay.hotelPrice + roomDay.hotelPrice,
              hotelTotal: resultDay.hotelTotal + roomDay.hotelTotal,
              packagedTotal: resultDay.packagedTotal + roomDay.packagedTotal,
              extraGuestSurcharge: resultDay.extraGuestSurcharge + roomDay.extraGuestSurcharge,
              flightPrice: resultDay.flightPrice + roomDay.flightPrice,
              surcharge: resultDay.surcharge + roomDay.surcharge,
              hotelAvailable: resultDay.hotelAvailable && roomDay.hotelAvailable,
              flightCacheActive: resultDay.flightCacheActive && roomDay.flightCacheActive,
              flightsAvailable: resultDay.flightsAvailable && roomDay.flightsAvailable,
              // keep the min value so that we know whether any package is sold out
              availableRooms: Math.min(resultDay.availableRooms, roomDay.availableRooms),
              pricing: addPricing(resultDay.pricing, roomDay.pricing),
              memberPricing: (resultDay.memberPricing && roomDay.memberPricing) ? addPricing(resultDay.memberPricing, roomDay.memberPricing) : undefined,
              pricingWithFlights: (resultDay.pricingWithFlights && roomDay.pricingWithFlights) ? addPricing(resultDay.pricingWithFlights, roomDay.pricingWithFlights) : undefined,
              memberPricingWithFlights: (resultDay.memberPricingWithFlights && roomDay.memberPricingWithFlights) ? addPricing(resultDay.memberPricingWithFlights, roomDay.memberPricingWithFlights) : undefined,
            })
          }) :
      undefined

    return resultSelectedDay
  },
)

/**
 * Returns whether some occupancy has no calendar loaded yet
 */
export const isMultiRoomFetchingCalendars = createSelector(
  (state: OfferPageState) => state,
  (state: OfferPageState, appState: App.State) => appState,
  (state, appState) => {
    const occupanciesStrings = (state.rooms ?? []).map(generateOccupancyStringByRoom)
    const calendars = appState.calendar.calendarsByOccupancy
    const isSomeOccupancyFetching = occupanciesStrings.some(occString => !calendars[occString])

    if (isSomeOccupancyFetching) return true

    const roomsStates = getOfferAndAppStateByRoom(state, appState)
    const roomsLoading =
      Object
        .values(roomsStates)
        .map(states => offerPageSelectorsIsFetchingCalendars(...states))

    const isSomeOccupancyPackageFetching = roomsLoading.some(Boolean)

    return isSomeOccupancyPackageFetching
  },
)

/**
 * Returns the availability of the days representing
 * the availability from all rooms for each day
 */
export const getMultiRoomDaysOtherAvailability = createSelector(
  (state: OfferPageState) => state,
  (state: OfferPageState, appState: App.State) => appState,
  (state, appState) => {
    const roomsStates = getOfferAndAppStateByRoom(state, appState)
    const roomsAvailabilities =
      Object
        .values(roomsStates)
        .map(states => offerPageSelectorsGetDaysOtherAvailability(...states))

    const resultAvailabilities: { [day: string]: boolean } = {}
    roomsAvailabilities
      .map(Object.entries)
      .flat()
      .forEach(([dayKey, isAvailable]) => {
        if (!(dayKey in resultAvailabilities)) {
          resultAvailabilities[dayKey] = isAvailable
        } else {
          resultAvailabilities[dayKey] = resultAvailabilities[dayKey] && isAvailable
        }
      })

    return resultAvailabilities
  },
)

/**
 * Returns the packages available filtered by
 * the selected duration and/or by the selected package's roomType
 */
export const getMultiRoomPackagesForFilters = createSelector(
  getOfferAndAppStateByRoom,
  (roomsStates) => {
    const roomsPackages =
      Object
        .values(roomsStates)
        .map(states => offerPageSelectorsGetPackagesForFilters(...states))
        .flat()

    return uniqueBy(roomsPackages, (pkg) => pkg.package.uniqueKey)
  },
)

/**
 * Returns the calendar days representing the
 * pricing and availability from all rooms
 */
export const getMultiRoomCalendarDays = createSelector(
  (state: OfferPageState) => state,
  (state: OfferPageState, appState: App.State) => appState,
  (state: OfferPageState, appState: App.State): Array<MonthDayView> => {
    const roomsStates = getOfferAndAppStateByRoom(state, appState)

    const calendarDaysByRoom = mergeObjects(
      Object
        .entries(roomsStates)
        .map(([roomId, states]) => ({ [roomId]: offerPageSelectorsGetCalendarDays(...states) })),
    )

    // Merge rooms calendarDays into a single array and then
    // merge all calendarDays with same "month year" key, returning an
    // object with all "month year"s and its calendarDays
    const mergedCalendarDays: { [monthYear: string]: Array<App.CalendarDay> } =
      Object
        .entries(calendarDaysByRoom)
        .map<Array<MonthDayView>>(([, calendarDays]) => {
          // We change the packagedTotal for each day if flights is selected for this room
          // so the calendar will show the correct price (considering that might have only one
          // room with flights selected)
          return calendarDays.map(([monthYear, calendar]) => {
            const groupedByTactical = groupBy(calendar.filter(d => d.hasTactical && d.hotelAvailable), day => day.monthKey)
            const firstCheckIn = moment(calendar[0].checkIn)
            const year = firstCheckIn.year()
            const monthIndex = firstCheckIn.month()
            const tacticalDays = Array.from(new Set((groupedByTactical.get(`${monthIndex + 1}-${year}`) ?? []).map(x => x.checkIn)).values())
            return [
              monthYear,
              calendar.map(day => ({ ...day, packagedTotal: state.flights ? day.packagedTotal : day.hotelTotal })),
              { year, monthIndex, tacticalDays },
            ]
          })
        })
        .flat()
        .reduce<Record<string, Array<App.CalendarDay>>>((mergedCalendars, calendarDay) => {
          if (!mergedCalendars[calendarDay[0]]) {
            // If the "month year" is not in the result object, adds it
            return ({
              ...mergedCalendars,
              [calendarDay[0]]: calendarDay[1],
            })
          }

          // If the "month year" is in the result object already,
          // merge its calendarDays
          return ({
            ...mergedCalendars,
            [calendarDay[0]]: mergedCalendars[calendarDay[0]].concat(calendarDay[1]),
          })
        }, {})

    // Now that we got the days from all rooms attached to its respective "month year" key,
    // we have to join the same days in calendarDays (each room has its own days),
    // and for each day, we build a single day with merged data from all rooms for that day
    // (e.g. each room has its price for day X, we sum up the prices from all rooms
    // so we get the total price for day X)
    const maybeMemberPriceMaybePrice = <T>(memberPrice: T, nonMemberPrice: T): T => state.displayMemberPrice ? memberPrice : nonMemberPrice

    const resultCalendarDays: Array<MonthDayView> =
      Object
        .keys(mergedCalendarDays)
        .map(calendarDayKey => {
          // Merges equal days into a single one
          const mergedCalendarDaysData: { [day: string]: App.CalendarDay } =
            mergedCalendarDays[calendarDayKey]
              .reduce<{ [day: string]: App.CalendarDay }>((mergedDays, calendarDay) => {
                if (!mergedDays[calendarDay.checkIn]) {
                  // If this day is not in the result object, adds it
                  return ({
                    ...mergedDays,
                    [calendarDay.checkIn]: calendarDay,
                  })
                }

                // If this day is in the result object already,
                // merge the data
                const addedDay = mergedDays[calendarDay.checkIn]
                const newDay = calendarDay
                const mergedDay: App.CalendarDay = {
                  ...addedDay,
                  // We don't care about the uniqueKey here, we just want the
                  // prices and availability
                  // @ts-ignore
                  uniqueKey: null,
                  // The same for roomId
                  // @ts-ignore
                  roomId: null,
                  canRequestDates: addedDay.canRequestDates && newDay.canRequestDates,
                  taxesAndFees: addedDay.taxesAndFees + newDay.taxesAndFees,
                  propertyFees: addedDay.propertyFees + newDay.propertyFees,
                  hotelValue: addedDay.hotelValue + newDay.hotelValue,
                  extraGuestSurcharge: addedDay.extraGuestSurcharge + newDay.extraGuestSurcharge,
                  flightPrice: addedDay.flightPrice + newDay.flightPrice,
                  surcharge: addedDay.surcharge + newDay.surcharge,
                  hotelAvailable: addedDay.hotelAvailable && newDay.hotelAvailable,
                  flightCacheActive: addedDay.flightCacheActive && newDay.flightCacheActive,
                  flightsAvailable: addedDay.flightsAvailable && newDay.flightsAvailable,
                  // keep the min value so that we know whether any package is sold out
                  availableRooms: Math.min(addedDay.availableRooms, newDay.availableRooms),
                  roomMemberPrices: addedDay.roomMemberPrices.concat(newDay.roomMemberPrices),
                  hotelMemberPrice: addedDay.hotelMemberPrice + newDay.hotelMemberPrice,
                  hotelMemberTotal: addedDay.hotelMemberTotal + newDay.hotelMemberTotal,
                  packagedMemberTotal: addedDay.packagedMemberTotal + newDay.packagedMemberTotal,
                  roomPrices: maybeMemberPriceMaybePrice(addedDay.roomMemberPrices.concat(newDay.roomMemberPrices), addedDay.roomPrices.concat(newDay.roomPrices)),
                  hotelPrice: maybeMemberPriceMaybePrice(addedDay.hotelMemberPrice + newDay.hotelMemberPrice, addedDay.hotelPrice + newDay.hotelPrice),
                  hotelTotal: maybeMemberPriceMaybePrice(addedDay.hotelMemberTotal + newDay.hotelMemberTotal, addedDay.hotelTotal + newDay.hotelTotal),
                  packagedTotal: maybeMemberPriceMaybePrice(addedDay.packagedMemberTotal + newDay.packagedMemberTotal, addedDay.packagedTotal + newDay.packagedTotal),
                  pricing: addPricing(addedDay.pricing, newDay.pricing),
                  memberPricing: (addedDay.memberPricing && newDay.memberPricing) ? addPricing(addedDay.memberPricing, newDay.memberPricing) : undefined,
                  pricingWithFlights: (addedDay.pricingWithFlights && newDay.pricingWithFlights) ? addPricing(addedDay.pricingWithFlights, newDay.pricingWithFlights) : undefined,
                  memberPricingWithFlights: (addedDay.memberPricingWithFlights && newDay.memberPricingWithFlights) ? addPricing(addedDay.memberPricingWithFlights, newDay.memberPricingWithFlights) : undefined,
                }
                return ({
                  ...mergedDays,
                  [calendarDay.checkIn]: mergedDay,
                })
              }, {})

          const groupedByTactical = groupBy(mergedCalendarDays[calendarDayKey].filter(d => d.hasTactical && d.hotelAvailable), day => day.monthKey)
          const firstDate = new Date(mergedCalendarDays[calendarDayKey][0].checkIn)
          const year = firstDate.getUTCFullYear()
          const monthIndex = firstDate.getUTCMonth()
          const tacticalDays = Array.from(new Set((groupedByTactical.get(`${monthIndex + 1}-${year}`) ?? []).map(x => x.checkIn)).values())

          const metadata = { year, monthIndex, tacticalDays }

          return [calendarDayKey, Object.values(mergedCalendarDaysData), metadata]
        })
    return resultCalendarDays
  },
)

/**
 * Returns all the duration options with
 * its respective lowest price from all rooms
 */
export const getMultiRoomDurationOptions = createSelector(
  (state: OfferPageState) => state,
  (state: OfferPageState, appState: App.State) => appState,
  (state, appState): Array<OfferDurationOption> => {
    const roomsStates = getOfferAndAppStateByRoom(state, appState)
    const roomsDurationOptions =
      Object
        .values(roomsStates)
        .map(states => getDurationOptions(states[1], states[0]))
    // Get available durations for each room (exclude sold out)
    const availableDurationsPerRoom = roomsDurationOptions.map(roomOptions =>
      new Set(roomOptions.filter(opt => !opt.soldOut).map(opt => opt.duration)),
    )

    // // Find intersection - only durations available for ALL rooms
    const commonDurations = availableDurationsPerRoom.reduce((intersection, roomDurations) => {
      return new Set([...intersection].filter(duration => roomDurations.has(duration)))
    })

    // // Filter each room's options to only include common durations
    const filteredRoomsDurationOptions = roomsDurationOptions.map((roomOptions) => roomOptions.filter(opt => commonDurations.has(opt.duration)))

    const resultDurationOptions = filteredRoomsDurationOptions
      .flat()
      .reduce<Record<number, OfferDurationOption>>((optionsByDuration, durationOption) => {
        if (!optionsByDuration[durationOption.duration]) {
          // If this duration is not in the result object, adds it
          return ({
            ...optionsByDuration,
            [durationOption.duration]: durationOption,
          })
        }

        // If this duration is in the result object already,
        // merge the data
        const addedDurationOption = optionsByDuration[durationOption.duration]
        const newDurationOption = durationOption

        return ({
          ...optionsByDuration,
          [durationOption.duration]: {
            ...addedDurationOption,
            lowestPrice: sum([addedDurationOption.lowestPrice, newDurationOption.lowestPrice], val => val ?? 0),
            lowestMemberPrice: sum([addedDurationOption.lowestMemberPrice, newDurationOption.lowestMemberPrice], val => val ?? 0),
            hasBonusInclusions: addedDurationOption.hasBonusInclusions && newDurationOption.hasBonusInclusions,
          },
        })
      }, {})

    return Object.values(resultDurationOptions)
  },
)

export const getMultiRoomPackageTotals = createSelector(
  getActivePackagesByRoom,
  (multipleRoomPackages): MultiRoomPackageTotals => {
    return Object.values(multipleRoomPackages || {}).reduce((acc, pkg) => {
      if (!pkg) return acc
      const {
        hotelPrice,
        hotelMemberPrice,
        flightPrice,
        propertyFees = 0,
        hotelValue,
      } = pkg

      return {
        hotelPrice: acc.hotelPrice + hotelPrice + propertyFees,
        hotelMemberPrice: hotelMemberPrice ? acc.hotelMemberPrice + hotelMemberPrice + propertyFees : 0,
        hotelPlusFlightsPrice: acc.hotelPlusFlightsPrice + hotelPrice + propertyFees + flightPrice,
        hotelPlusFlightsMemberPrice: hotelMemberPrice ? acc.hotelPlusFlightsMemberPrice + hotelMemberPrice + propertyFees + flightPrice : 0,
        flightPrice: acc.flightPrice + flightPrice,
        hotelValue: acc.hotelValue + hotelValue,
        shouldDisplayValue: acc.shouldDisplayValue && pkg.package.shouldDisplayValue,
      }
    }, { hotelPrice: 0, hotelMemberPrice: 0, hotelPlusFlightsPrice: 0, hotelPlusFlightsMemberPrice: 0, flightPrice: 0, hotelValue: 0, shouldDisplayValue: true })
  },
)
